import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import {
  ArrowLeft,
  Check,
  ChefHat,
  CircleCheck,
  Clock3,
  Coffee,
  Download,
  Minus,
  Plus,
  QrCode,
  ReceiptText,
  RefreshCw,
  ShoppingBag,
  Sparkles,
  UtensilsCrossed,
  X,
} from "lucide-react";
import { api, ApiRequestError } from "../lib/api";
import { formatRupiah } from "../lib/format";
import { menuImageFor } from "../lib/menu-images";
import { useRealtime } from "../lib/realtime";
import { RealtimeBadge } from "../components/RealtimeBadge";
import type {
  MenuModifierOption,
  MenuVariant,
  OrderView,
  PaymentView,
  PublicMenuItem,
  PublicTableResponse,
} from "../lib/types";

type CartLine = {
  key: string;
  item: PublicMenuItem;
  qty: number;
  variant: MenuVariant | null;
  options: MenuModifierOption[];
  notes: string;
  unitPrice: number;
};

type Step = "menu" | "checkout" | "payment" | "tracker";

function lineKey(
  item: PublicMenuItem,
  variant: MenuVariant | null,
  options: MenuModifierOption[],
  notes: string,
): string {
  return [
    item.id,
    variant?.id ?? 0,
    options
      .map((option) => option.id)
      .sort((left, right) => left - right)
      .join("-"),
    notes.trim(),
  ].join("|");
}

function formatCountdown(milliseconds: number): string {
  const total = Math.max(0, Math.floor(milliseconds / 1000));
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}

const itemStatusLabels: Record<string, string> = {
  queued: "Menunggu",
  preparing: "Dimasak",
  ready: "Siap",
  served: "Disajikan",
  void: "Dibatalkan",
};

const statusCopy: Record<string, { title: string; body: string }> = {
  awaiting_payment: {
    title: "Menunggu pembayaran",
    body: "Selesaikan pembayaran QRIS untuk mengirim pesanan ke dapur.",
  },
  queued: {
    title: "Pesanan diterima dapur",
    body: "Dapur sudah menerima pesananmu dan akan segera mulai memasak.",
  },
  preparing: {
    title: "Sedang disiapkan",
    body: "Pesananmu sedang dimasak. Kami akan kabari saat siap.",
  },
  ready: {
    title: "Siap disajikan",
    body: "Pesananmu sudah siap. Pelayan akan mengantarkannya ke meja.",
  },
  served: {
    title: "Sudah disajikan",
    body: "Selamat menikmati. Terima kasih sudah memesan!",
  },
  completed: {
    title: "Pesanan selesai",
    body: "Terima kasih sudah mampir. Sampai jumpa lagi!",
  },
  cancelled: {
    title: "Pesanan dibatalkan",
    body: "Pesanan ini dibatalkan. Silakan hubungi staf jika perlu bantuan.",
  },
};

export function CustomerPage() {
  const { token = "" } = useParams();
  const [searchParams] = useSearchParams();
  const requestedOrderId = searchParams.get("order")?.trim() ?? "";
  const [table, setTable] = useState<PublicTableResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState<Step>("menu");
  const [cart, setCart] = useState<CartLine[]>([]);
  const [activeItem, setActiveItem] = useState<PublicMenuItem | null>(null);
  const [phone, setPhone] = useState(
    () => window.localStorage.getItem("cafe_phone") ?? "",
  );
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [order, setOrder] = useState<OrderView | null>(null);
  const [payment, setPayment] = useState<PaymentView | null>(null);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [cancelConfirm, setCancelConfirm] = useState(false);
  const [qrDownloaded, setQrDownloaded] = useState(false);
  const [now, setNow] = useState(Date.now());
  const clientOrderId = useRef<string>(crypto.randomUUID());
  const audioRef = useRef<AudioContext | null>(null);
  const [soundReady, setSoundReady] = useState(false);

  const loadTable = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const data = await api<PublicTableResponse>("/api/public/table", {
        headers: { "x-table-token": token },
      });
      setTable(data);

      // Link dari WhatsApp membawa ?order=<public_id> supaya pesanan tertentu
      // bisa dibuka di perangkat lain, bukan hanya mengandalkan localStorage.
      if (requestedOrderId) {
        try {
          const requested = await api<{ order: OrderView }>(
            `/api/public/orders/${requestedOrderId}`,
            { headers: { "x-table-token": token } },
          );
          setOrder(requested.order);
          setPayment(requested.order.payment);
          setStep(
            requested.order.paymentStatus === "paid" ||
              requested.order.status === "completed" ||
              requested.order.status === "cancelled"
              ? "tracker"
              : "payment",
          );
          return;
        } catch {
          // Link lama/tidak valid: lanjut ke pemulihan order lokal.
        }
      }

      // Pulihkan pesanan terakhir setelah refresh. Tanpa ini, pelanggan yang
      // menutup tab saat halaman QRIS terbuka kehilangan jejak pesanannya.
      const storageKey = `cafe_last_order_${token}`;
      const savedId = window.localStorage.getItem(storageKey);
      if (savedId) {
        try {
          const saved = await api<{ order: OrderView }>(
            `/api/public/orders/${savedId}`,
            { headers: { "x-table-token": token } },
          );
          if (
            saved.order.status === "completed" ||
            saved.order.status === "cancelled"
          ) {
            window.localStorage.removeItem(storageKey);
          } else {
            setOrder(saved.order);
            setPayment(saved.order.payment);
            setStep(
              saved.order.paymentStatus === "paid" ? "tracker" : "payment",
            );
          }
        } catch {
          window.localStorage.removeItem(storageKey);
        }
      }
    } catch (requestError) {
      setError(
        requestError instanceof ApiRequestError
          ? requestError.message
          : "Tidak dapat memuat menu. Periksa koneksi lalu coba lagi.",
      );
    } finally {
      setLoading(false);
    }
  }, [requestedOrderId, token]);

  useEffect(() => {
    void loadTable();
  }, [loadTable]);

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  const refreshOrder = useCallback(async () => {
    if (!order) return;
    try {
      const response = await api<{ order: OrderView; serverTime: string }>(
        `/api/public/orders/${order.id}`,
        { headers: { "x-table-token": token } },
      );
      setOrder(response.order);
      setPayment(response.order.payment);
      if (
        response.order.paymentStatus === "paid" ||
        response.order.status === "cancelled"
      ) {
        setStep("tracker");
      }
    } catch {
      // Refresh gagal sesaat bukan alasan menampilkan error besar.
    }
  }, [order, token]);

  const realtime = useRealtime({
    customerToken: token,
    orderId: order?.id ?? null,
    enabled: Boolean(token && order?.id),
    onEvent: () => {
      void refreshOrder();
    },
  });

  // Fallback lambat hanya saat WebSocket terputus. Saat realtime aktif,
  // perubahan status datang dari event server tanpa polling.
  useEffect(() => {
    if (!order) return;
    if (
      order.paymentStatus === "paid" ||
      order.status === "cancelled" ||
      realtime.connected
    ) {
      return;
    }
    const timer = window.setInterval(() => void refreshOrder(), 15_000);
    return () => window.clearInterval(timer);
  }, [order, refreshOrder, realtime.connected]);

  const cartCount = cart.reduce((sum, line) => sum + line.qty, 0);
  const subtotal = cart.reduce(
    (sum, line) => sum + line.unitPrice * line.qty,
    0,
  );
  const taxPercent = table?.outlet.taxPercent ?? 0;
  const servicePercent = table?.outlet.serviceChargePercent ?? 0;
  const tax = Math.round((subtotal * taxPercent) / 100);
  const service = Math.round((subtotal * servicePercent) / 100);
  const total = subtotal + tax + service;

  const expiresIn = payment?.expiresAt
    ? new Date(payment.expiresAt).getTime() - now
    : 0;
  // Prioritaskan state payment terbaru. order.paymentStatus bisa masih
  // "expired" dari QR lama saat createPayment() baru saja membuat QR baru.
  const isPaymentExpired = payment
    ? payment.status === "expired" ||
      (payment.expiresAt
        ? new Date(payment.expiresAt).getTime() <= now
        : false)
    : order?.paymentStatus === "expired";

  // Hapus QR dari state segera setelah kedaluwarsa. Server juga tidak lagi
  // mengirim qrDataUrl/qrisPayload untuk payment yang sudah lewat expires_at.
  useEffect(() => {
    if (!isPaymentExpired || !payment) return;
    if (
      payment.status === "expired" &&
      !payment.qrDataUrl &&
      !payment.qrisPayload
    ) {
      return;
    }
    setPayment((current) =>
      current
        ? {
            ...current,
            status: "expired",
            qrDataUrl: null,
            qrisPayload: null,
          }
        : current,
    );
  }, [isPaymentExpired, payment]);

  function addToCart(
    item: PublicMenuItem,
    variant: MenuVariant | null,
    options: MenuModifierOption[],
    lineNotes: string,
  ) {
    const unitPrice =
      item.price +
      (variant?.priceDelta ?? 0) +
      options.reduce((sum, option) => sum + option.priceDelta, 0);
    const key = lineKey(item, variant, options, lineNotes);
    setCart((current) => {
      const found = current.find((line) => line.key === key);
      if (found) {
        return current.map((line) =>
          line.key === key ? { ...line, qty: line.qty + 1 } : line,
        );
      }
      return [
        ...current,
        {
          key,
          item,
          qty: 1,
          variant,
          options,
          notes: lineNotes,
          unitPrice,
        },
      ];
    });
    setActiveItem(null);
  }

  function changeQty(key: string, delta: number) {
    setCart((current) =>
      current
        .map((line) =>
          line.key === key ? { ...line, qty: line.qty + delta } : line,
        )
        .filter((line) => line.qty > 0),
    );
  }

  function unlockSound() {
    try {
      audioRef.current = new AudioContext();
      void audioRef.current.resume();
      setSoundReady(true);
    } catch {
      setSoundReady(false);
    }
  }

  function notifyReady() {
    if (!audioRef.current || !soundReady) return;
    const context = audioRef.current;
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    oscillator.type = "sine";
    oscillator.frequency.value = 880;
    gain.gain.value = 0.05;
    oscillator.connect(gain);
    gain.connect(context.destination);
    oscillator.start();
    oscillator.stop(context.currentTime + 0.18);
  }

  useEffect(() => {
    if (order?.status === "ready") notifyReady();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order?.status]);

  async function submitOrder() {
    if (cart.length === 0) return;
    setBusy(true);
    setError(null);
    setPaymentError(null);
    setNotice(null);
    try {
      window.localStorage.setItem("cafe_phone", phone);
      const created = await api<{ order: OrderView }>("/api/public/orders", {
        method: "POST",
        headers: { "x-table-token": token },
        body: JSON.stringify({
          phone,
          notes: notes.trim() || undefined,
          clientOrderId: clientOrderId.current,
          items: cart.map((line) => ({
            menuItemId: line.item.id,
            qty: line.qty,
            variantId: line.variant?.id,
            modifierOptionIds: line.options.map((option) => option.id),
            notes: line.notes || undefined,
          })),
        }),
      });
      setOrder(created.order);
      window.localStorage.setItem(
        `cafe_last_order_${token}`,
        created.order.id,
      );
      setStep("payment");
      await createPayment(created.order);
    } catch (requestError) {
      setError(
        requestError instanceof ApiRequestError
          ? requestError.message
          : "Pesanan gagal dikirim. Coba lagi.",
      );
    } finally {
      setBusy(false);
    }
  }

  async function createPayment(currentOrder: OrderView) {
    setBusy(true);
    setPaymentError(null);
    try {
      const response = await api<{
        alreadyPaid: boolean;
        payment?: PaymentView;
        order?: OrderView;
      }>(`/api/public/orders/${currentOrder.id}/payment`, {
        method: "POST",
        headers: { "x-table-token": token },
      });
      if (response.payment) {
        setPayment(response.payment);
        setQrDownloaded(false);
      }
      if (response.order) {
        setOrder(response.order);
      } else if (response.payment && !response.alreadyPaid) {
        // Respons create payment tidak selalu membawa order. Jangan biarkan
        // order.paymentStatus lama ("expired") menutupi QR baru.
        setOrder((current) =>
          current
            ? {
                ...current,
                paymentStatus: "pending",
                status: "awaiting_payment",
              }
            : current,
        );
      }
      if (response.alreadyPaid) setStep("tracker");
      else setStep("payment");
    } catch (requestError) {
      setPaymentError(
        requestError instanceof ApiRequestError
          ? requestError.message
          : "QRIS gagal dibuat. Coba lagi atau panggil staf.",
      );
      setStep("payment");
    } finally {
      setBusy(false);
    }
  }

  async function cancelOrder() {
    if (!order) return;
    setBusy(true);
    setPaymentError(null);
    try {
      await api<{ cancelled: boolean }>(
        `/api/public/orders/${order.id}/cancel`,
        {
          method: "POST",
          headers: { "x-table-token": token },
        },
      );
      setCancelConfirm(false);
      setNotice("Pesanan dibatalkan. Silakan pilih menu lagi.");
      resetOrder();
    } catch (requestError) {
      setCancelConfirm(false);
      setPaymentError(
        requestError instanceof ApiRequestError
          ? requestError.message
          : "Pesanan gagal dibatalkan. Coba lagi atau panggil staf.",
      );
      void refreshOrder();
    } finally {
      setBusy(false);
    }
  }

  function resetOrder() {
    setCart([]);
    setOrder(null);
    setPayment(null);
    setPaymentError(null);
    setCancelConfirm(false);
    setNotes("");
    setStep("menu");
    if (token) window.localStorage.removeItem(`cafe_last_order_${token}`);
    clientOrderId.current = crypto.randomUUID();
    void loadTable();
  }

  const categories = table?.menu ?? [];
  const [activeCategory, setActiveCategory] = useState<number | null>(null);
  useEffect(() => {
    if (!activeCategory && categories[0]) setActiveCategory(categories[0].id);
  }, [activeCategory, categories]);

  const visibleItems = useMemo(() => {
    if (!activeCategory) return categories.flatMap((category) => category.items);
    return (
      categories.find((category) => category.id === activeCategory)?.items ?? []
    );
  }, [activeCategory, categories]);

  if (loading) {
    return (
      <div className="customer-shell">
        <div className="customer-loading">
          <Coffee size={28} />
          <strong>Menyiapkan menu...</strong>
        </div>
      </div>
    );
  }

  if (error && !table) {
    return (
      <div className="customer-shell">
        <div className="customer-error card">
          <QrCode size={38} />
          <h1>QR tidak dikenali</h1>
          <p>{error}</p>
          <button type="button" className="btn btn-primary" onClick={() => void loadTable()}>
            <RefreshCw size={16} /> Coba lagi
          </button>
        </div>
      </div>
    );
  }

  if (!table) return null;

  return (
    <div className="customer-shell">
      <header className="customer-top">
        <div>
          <span className="customer-top__eyebrow">{table.tenant.name}</span>
          <strong>{table.outlet.name}</strong>
          <span className="customer-top__meta">
            {table.unit.label}
            {table.unit.zone ? ` · ${table.unit.zone}` : ""}
          </span>
        </div>
        <div className="customer-top__badges">

          {order ? <RealtimeBadge connected={realtime.connected} /> : null}
          <button
            type="button"
            className="icon-button"
            onClick={unlockSound}
            title={soundReady ? "Suara notifikasi aktif" : "Aktifkan suara notifikasi"}
            aria-label="Aktifkan suara notifikasi"
          >
            <Sparkles size={17} />
          </button>
        </div>
      </header>

      {notice ? (
        <div className="alert alert--success" role="status">
          {notice}
        </div>
      ) : null}

      {step === "menu" ? (
        <>
          <section className="customer-hero">
            <p className="eyebrow">Scan · pilih · bayar</p>
            <h1>Mau pesan apa hari ini?</h1>
            <p>
              Pesanan langsung masuk ke dapur setelah QRIS terbayar. Nomor meja{" "}
              <strong>{table.unit.label}</strong> sudah otomatis.
            </p>
          </section>

          <nav className="category-chips" aria-label="Kategori menu">
            {categories.map((category) => (
              <button
                key={category.id}
                type="button"
                className={`filter-chip${activeCategory === category.id ? " is-active" : ""}`}
                onClick={() => setActiveCategory(category.id)}
              >
                {category.name}
              </button>
            ))}
          </nav>

          <main className="customer-menu">
            {visibleItems.length === 0 ? (
              <div className="empty-state empty-state--compact">
                <UtensilsCrossed size={28} />
                <h3>Belum ada menu</h3>
                <p>Menu untuk kategori ini belum tersedia.</p>
              </div>
            ) : (
              visibleItems.map((item) => (
                <article
                  key={item.id}
                  className={`menu-item${item.available ? "" : " is-soldout"}`}
                >
                  <div className="menu-item__media">
                    <img
                      src={menuImageFor(item.name, item.imageUrl)}
                      alt={item.name}
                      loading="lazy"
                      onError={(event) => {
                        event.currentTarget.onerror = null;
                        event.currentTarget.src = menuImageFor(item.name, null);
                      }}
                    />
                  </div>
                  <div className="menu-item__body">
                    <div className="menu-item__head">
                      <h3>{item.name}</h3>
                      <strong>{formatRupiah(item.price)}</strong>
                    </div>
                    {item.description ? <p>{item.description}</p> : null}
                    <div className="menu-item__foot">
                      <span className="menu-item__station">
                        {item.station === "bar" ? "Minuman" : item.station === "both" ? "Dapur & bar" : "Dapur"}
                      </span>
                      <button
                        type="button"
                        className="btn btn-primary btn--sm"
                        disabled={!item.available}
                        onClick={() => {
                          if (
                            item.variants.length === 0 &&
                            item.modifierGroups.length === 0
                          ) {
                            addToCart(item, null, [], "");
                          } else {
                            setActiveItem(item);
                          }
                        }}
                      >
                        {item.available ? <Plus size={15} /> : null}
                        {item.available ? "Tambah" : "Habis"}
                      </button>
                    </div>
                  </div>
                </article>
              ))
            )}
          </main>

          {cartCount > 0 ? (
            <button type="button" className="cart-bar" onClick={() => setStep("checkout")}>
              <span className="cart-bar__count">
                <ShoppingBag size={17} /> {cartCount} item
              </span>
              <span className="cart-bar__total">{formatRupiah(total)}</span>
              <span className="cart-bar__action">
                Lihat pesanan <ArrowLeft size={16} className="cart-bar__arrow" />
              </span>
            </button>
          ) : null}
        </>
      ) : null}

      {step === "checkout" ? (
        <section className="customer-panel">
          <button type="button" className="auth-back" onClick={() => setStep("menu")}>
            <ArrowLeft size={15} /> Kembali ke menu
          </button>
          <p className="eyebrow">Konfirmasi</p>
          <h1 className="customer-panel__title">Pesananmu</h1>

          <div className="checkout-lines">
            {cart.map((line) => (
              <div className="checkout-line" key={line.key}>
                <div>
                  <strong>{line.item.name}</strong>
                  {line.variant ? <span className="checkout-line__meta">{line.variant.name}</span> : null}
                  {line.options.length > 0 ? (
                    <span className="checkout-line__meta">
                      {line.options.map((option) => option.name).join(", ")}
                    </span>
                  ) : null}
                  {line.notes ? <span className="checkout-line__meta">“{line.notes}”</span> : null}
                  <span className="checkout-line__price">{formatRupiah(line.unitPrice)}</span>
                </div>
                <div className="qty-control">
                  <button type="button" onClick={() => changeQty(line.key, -1)} aria-label="Kurangi">
                    <Minus size={15} />
                  </button>
                  <span>{line.qty}</span>
                  <button type="button" onClick={() => changeQty(line.key, 1)} aria-label="Tambah">
                    <Plus size={15} />
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="checkout-summary">
            <div><span>Subtotal</span><span>{formatRupiah(subtotal)}</span></div>
            {tax > 0 ? <div><span>Pajak</span><span>{formatRupiah(tax)}</span></div> : null}
            {service > 0 ? <div><span>Service</span><span>{formatRupiah(service)}</span></div> : null}
            <div className="checkout-summary__total"><span>Total</span><strong>{formatRupiah(total)}</strong></div>
          </div>

          <label className="field field--full">
            <span>Nomor WhatsApp</span>
            <input
              className="input"
              inputMode="tel"
              autoComplete="tel"
              placeholder="08xxxxxxxxxx"
              value={phone}
              onChange={(event) => setPhone(event.target.value)}
            />
            <small className="fine-print">Untuk struk dan kabar status pesanan.</small>
          </label>

          <label className="field field--full">
            <span>Catatan untuk dapur (opsional)</span>
            <textarea
              className="input"
              rows={2}
              maxLength={500}
              placeholder="Contoh: tanpa sambal, es sedikit"
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
            />
          </label>

          {error ? <div className="alert alert--danger">{error}</div> : null}

          <button
            type="button"
            className="btn btn-primary btn-block customer-submit"
            disabled={busy || cart.length === 0}
            onClick={() => void submitOrder()}
          >
            {busy ? "Mengirim pesanan..." : `Bayar ${formatRupiah(total)}`}
          </button>
          <p className="fine-print customer-panel__note">
            Dengan melanjutkan, pesanan dibuat dan QRIS muncul di layar berikutnya.
          </p>
        </section>
      ) : null}

      {step === "payment" ? (
        <section className="customer-panel payment-panel">
          <button
            type="button"
            className="auth-back"
            onClick={() => setCancelConfirm(true)}
          >
            <ArrowLeft size={15} /> Kembali ke menu
          </button>
          <p className="eyebrow">Pembayaran</p>
          <h1 className="customer-panel__title">
            {order?.orderNo ? `Pesanan #${order.orderNo}` : "Selesaikan pembayaran"}
          </h1>
          <p className="customer-panel__lead">
            {table.unit.label} · {order?.items.length ?? 0} item
          </p>

          {paymentError ? (
            <div className="alert alert--danger">{paymentError}</div>
          ) : null}

          {isPaymentExpired ? (
            <div className="alert alert--warning">
              <strong>QRIS sudah kedaluwarsa.</strong>
              <p>
                QR lama sudah dihapus agar tidak ada pembayaran ke QR yang tidak
                berlaku. Buat QR baru, atau batalkan pesanan dan kembali ke menu.
              </p>
            </div>
          ) : null}

          {!isPaymentExpired && payment?.qrDataUrl ? (
            <div className="qr-card">
              <img src={payment.qrDataUrl} alt="QRIS pembayaran" />
              <div className="qr-card__amount">
                <span>Total bayar</span>
                <strong>{formatRupiah(payment.totalAmount)}</strong>
                {payment.uniqueCode > 0 ? (
                  <small>Termasuk kode unik {payment.uniqueCode}</small>
                ) : null}
              </div>
              <div className={`qr-card__timer${expiresIn <= 60_000 ? " is-urgent" : ""}`}>
                <Clock3 size={15} />
                {expiresIn > 0 ? `Berlaku ${formatCountdown(expiresIn)}` : "QR kedaluwarsa"}
              </div>
              <div className="qr-card__actions">
                <a
                  className="btn btn-secondary btn--sm"
                  href={payment.qrDataUrl}
                  download={`QRIS-${order?.orderNo ?? "pembayaran"}.png`}
                  onClick={() => setQrDownloaded(true)}
                >
                  <Download size={15} /> Unduh QRIS
                </a>
              </div>
              <p className="fine-print qr-card__hint">
                Membayar dari HP yang sama? Unduh QR ini, lalu buka aplikasi
                e-wallet/bank dan pilih Scan/Upload dari galeri.
              </p>
              {qrDownloaded ? (
                <p className="fine-print qr-card__hint">
                  Jika file tidak muncul di galeri, tekan lama gambar QR lalu
                  pilih <strong>Simpan Gambar</strong>.
                </p>
              ) : null}
            </div>
          ) : (
            <div className="empty-state empty-state--compact">
              <QrCode size={34} />
              <h3>{isPaymentExpired ? "QRIS kedaluwarsa" : "QRIS belum muncul"}</h3>
              <p>
                {isPaymentExpired
                  ? "QR lama sudah dihapus. Buat QR baru untuk melanjutkan pembayaran."
                  : "Cek status pembayaran. Jika masih belum muncul, batalkan pesanan dan pesan ulang."}
              </p>
            </div>
          )}

          <div className="payment-status">
            <span className={`status-dot${order?.paymentStatus === "paid" ? " is-paid" : ""}`} />
            <div>
              <strong>
                {order?.paymentStatus === "paid"
                  ? "Pembayaran diterima"
                  : isPaymentExpired
                    ? "QRIS kedaluwarsa"
                    : "Menunggu pembayaran"}
              </strong>
              <span>
                {order?.paymentStatus === "paid"
                  ? "Pesanan diteruskan ke dapur."
                  : isPaymentExpired
                    ? "Buat QR baru atau batalkan pesanan."
                    : "Halaman ini mengecek status otomatis."}
              </span>
            </div>
          </div>

          {cancelConfirm ? (
            <div className="alert alert--warning">
              <strong>Batalkan pesanan?</strong>
              <p>
                QRIS yang sudah dibuat akan dihapus dan pesanan dibatalkan. Kamu
                akan kembali ke menu untuk memesan ulang.
              </p>
              <div className="payment-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  disabled={busy}
                  onClick={() => setCancelConfirm(false)}
                >
                  Tidak
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={busy}
                  onClick={() => void cancelOrder()}
                >
                  {busy ? "Membatalkan..." : "Ya, batalkan"}
                </button>
              </div>
            </div>
          ) : null}

          <div className="payment-actions">
            <button type="button" className="btn btn-secondary" onClick={() => void refreshOrder()}>
              <RefreshCw size={16} /> Cek status
            </button>
            {order && isPaymentExpired ? (
              <button
                type="button"
                className="btn btn-primary"
                disabled={busy}
                onClick={() => void createPayment(order)}
              >
                <RefreshCw size={16} /> Buat QRIS Baru
              </button>
            ) : null}
            {order && order.paymentStatus !== "paid" ? (
              <button
                type="button"
                className="btn btn-secondary"
                disabled={busy}
                onClick={() => setCancelConfirm(true)}
              >
                <X size={16} /> Batalkan Pesanan
              </button>
            ) : null}
          </div>
          <p className="fine-print customer-panel__note">
            {isPaymentExpired
              ? "QR lama sudah tidak berlaku dan tidak ditampilkan lagi."
              : "Pembayaran diverifikasi otomatis oleh WebQRIS. Jangan tutup halaman ini sebelum status berubah."}
          </p>
        </section>
      ) : null}

      {step === "tracker" && order ? (
        <section className="customer-panel">
          <p className="eyebrow">Status pesanan</p>
          <h1 className="customer-panel__title">
            {statusCopy[order.status]?.title ?? "Pesanan diproses"}
          </h1>
          <p className="customer-panel__lead">
            {order.orderNo ? `#${order.orderNo} · ` : ""}
            {table.unit.label}
          </p>

          <div className="tracker">
            {[
              { key: "queued", label: "Diterima dapur", icon: ReceiptText },
              { key: "preparing", label: "Dimasak", icon: ChefHat },
              { key: "ready", label: "Siap", icon: BellIcon },
              { key: "served", label: "Disajikan", icon: CircleCheck },
            ].map((entry) => {
              const orderIndex = ["queued", "preparing", "ready", "served"].indexOf(order.status);
              const entryIndex = ["queued", "preparing", "ready", "served"].indexOf(entry.key);
              const done = orderIndex >= entryIndex && orderIndex >= 0;
              const Icon = entry.icon;
              return (
                <div key={entry.key} className={`tracker__step${done ? " is-done" : ""}`}>
                  <span className="tracker__icon"><Icon size={16} /></span>
                  <span>{entry.label}</span>
                </div>
              );
            })}
          </div>

          <div className="tracker-note">
            <strong>{statusCopy[order.status]?.title ?? "Pesanan diproses"}</strong>
            <p>{statusCopy[order.status]?.body ?? "Kami akan memperbarui status di halaman ini."}</p>
          </div>

          <div className="checkout-lines">
            {order.items.map((item) => (
              <div className="checkout-line checkout-line--static" key={item.id}>
                <div>
                  <strong>{item.qty}× {item.name}</strong>
                  {item.notes ? <span className="checkout-line__meta">“{item.notes}”</span> : null}
                </div>
                  <span className={`item-status item-status--${item.status}`}>
                    {itemStatusLabels[item.status] ?? item.status}
                  </span>
              </div>
            ))}
          </div>

          {order.totals ? (
            <div className="checkout-summary">
              <div><span>Subtotal</span><span>{formatRupiah(order.totals.subtotal)}</span></div>
              {order.totals.tax > 0 ? <div><span>Pajak</span><span>{formatRupiah(order.totals.tax)}</span></div> : null}
              {order.totals.serviceCharge > 0 ? <div><span>Service</span><span>{formatRupiah(order.totals.serviceCharge)}</span></div> : null}
              <div className="checkout-summary__total"><span>Total</span><strong>{formatRupiah(order.totals.total)}</strong></div>
            </div>
          ) : null}

          <div className="payment-actions">
            <button type="button" className="btn btn-secondary" onClick={() => void refreshOrder()}>
              <RefreshCw size={16} /> Perbarui status
            </button>
            {order.status === "served" || order.status === "completed" ? (
              <button type="button" className="btn btn-primary" onClick={resetOrder}>
                <Plus size={16} /> Pesan lagi
              </button>
            ) : null}
          </div>
        </section>
      ) : null}

      {activeItem ? (
        <ItemSheet
          item={activeItem}
          onClose={() => setActiveItem(null)}
          onAdd={addToCart}
        />
      ) : null}
    </div>
  );
}

function BellIcon({ size }: { size: number }) {
  return <Check size={size} />;
}

function ItemSheet({
  item,
  onClose,
  onAdd,
}: {
  item: PublicMenuItem;
  onClose: () => void;
  onAdd: (
    item: PublicMenuItem,
    variant: MenuVariant | null,
    options: MenuModifierOption[],
    notes: string,
  ) => void;
}) {
  const [variant, setVariant] = useState<MenuVariant | null>(
    item.variants[0] ?? null,
  );
  const [selected, setSelected] = useState<Record<number, number[]>>({});
  const [notes, setNotes] = useState("");
  const [error, setError] = useState<string | null>(null);

  const selectedOptions = useMemo(() => {
    const result: MenuModifierOption[] = [];
    for (const group of item.modifierGroups) {
      const ids = selected[group.id] ?? [];
      for (const option of group.options) {
        if (ids.includes(option.id)) result.push(option);
      }
    }
    return result;
  }, [item.modifierGroups, selected]);

  const unitPrice =
    item.price +
    (variant?.priceDelta ?? 0) +
    selectedOptions.reduce((sum, option) => sum + option.priceDelta, 0);

  function toggleOption(groupId: number, optionId: number, maxSelect: number) {
    setSelected((current) => {
      const existing = current[groupId] ?? [];
      if (existing.includes(optionId)) {
        return { ...current, [groupId]: existing.filter((id) => id !== optionId) };
      }
      if (maxSelect === 1) return { ...current, [groupId]: [optionId] };
      if (existing.length >= maxSelect) return current;
      return { ...current, [groupId]: [...existing, optionId] };
    });
  }

  function submit() {
    if (item.variants.length > 0 && !variant) {
      setError("Pilih varian terlebih dahulu.");
      return;
    }
    for (const group of item.modifierGroups) {
      const count = (selected[group.id] ?? []).length;
      if (count < group.minSelect) {
        setError(`Pilih minimal ${group.minSelect} untuk ${group.name}.`);
        return;
      }
    }
    onAdd(item, variant, selectedOptions, notes);
  }

  return (
    <div className="sheet-backdrop" role="presentation" onClick={onClose}>
      <div
        className="sheet"
        role="dialog"
        aria-modal="true"
        aria-label={item.name}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="sheet__head">
          <div>
            <p className="eyebrow">Tambahkan</p>
            <h2>{item.name}</h2>
            <span>{formatRupiah(unitPrice)}</span>
          </div>
          <button type="button" className="icon-button" onClick={onClose} aria-label="Tutup">
            <X size={18} />
          </button>
        </div>

        <div className="sheet__body">
          {item.description ? <p className="sheet__description">{item.description}</p> : null}

          {item.variants.length > 0 ? (
            <div className="option-group">
              <strong>Varian</strong>
              <div className="option-list">
                {item.variants.map((entry) => (
                  <button
                    key={entry.id}
                    type="button"
                    className={`option-chip${variant?.id === entry.id ? " is-active" : ""}`}
                    onClick={() => setVariant(entry)}
                  >
                    {entry.name}
                    {entry.priceDelta !== 0 ? (
                      <span>{entry.priceDelta > 0 ? "+" : ""}{formatRupiah(entry.priceDelta)}</span>
                    ) : null}
                  </button>
                ))}
              </div>
            </div>
          ) : null}

          {item.modifierGroups.map((group) => (
            <div className="option-group" key={group.id}>
              <strong>
                {group.name}
                <small>
                  {group.minSelect > 0 ? ` wajib ${group.minSelect}` : ""}
                  {group.maxSelect > 1 ? ` maks ${group.maxSelect}` : ""}
                </small>
              </strong>
              <div className="option-list">
                {group.options.map((option) => {
                  const active = (selected[group.id] ?? []).includes(option.id);
                  return (
                    <button
                      key={option.id}
                      type="button"
                      className={`option-chip${active ? " is-active" : ""}`}
                      onClick={() => toggleOption(group.id, option.id, group.maxSelect)}
                    >
                      {option.name}
                      {option.priceDelta !== 0 ? (
                        <span>{option.priceDelta > 0 ? "+" : ""}{formatRupiah(option.priceDelta)}</span>
                      ) : null}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}

          <label className="field field--full">
            <span>Catatan (opsional)</span>
            <textarea
              className="input"
              rows={2}
              maxLength={200}
              placeholder="Contoh: tanpa gula"
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
            />
          </label>

          {error ? <div className="alert alert--danger">{error}</div> : null}
        </div>

        <div className="sheet__foot">
          <button type="button" className="btn btn-primary btn-block" onClick={submit}>
            Tambah · {formatRupiah(unitPrice)}
          </button>
        </div>
      </div>
    </div>
  );
}
